import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { api } from '@/lib/api'
import { useMerchants, useUsers, QUERY_KEYS } from '@/hooks/use-queries'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle, Package } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CreateMerchantDialog } from '@/components/backoffice/create-merchant-dialog'
import { CreateUserDialog } from '@/components/backoffice/create-user-dialog'
import { toast } from 'sonner'
import type { Merchant, User } from '@/lib/types'

export const Route = createFileRoute('/_app/backoffice/merchants')({
  component: BackofficePage,
})

function BackofficePage() {
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Backoffice</h1>
          <p className="text-sm text-muted-foreground">Controle total do sistema Rafobase</p>
        </div>
      </div>

      <Tabs defaultValue="merchants" className="w-full">
        <TabsList>
          <TabsTrigger value="merchants">Lojistas</TabsTrigger>
          <TabsTrigger value="users">Usuários</TabsTrigger>
        </TabsList>

        <TabsContent value="merchants" className="space-y-4">
          <div className="flex justify-end">
            <CreateMerchantDialog />
          </div>
          <MerchantsTable />
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <div className="flex justify-end">
            <CreateUserDialog />
          </div>
          <UsersTable />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function MerchantsTable() {
  const { data: merchants, isLoading, error, refetch } = useMerchants()

  if (isLoading) {
    return <TableSkeleton cols={4} />
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Erro ao carregar dados</AlertTitle>
        <AlertDescription className="flex flex-col gap-2">
          <p>{(error as Error).message}</p>
          <ChonkyButton
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            className="w-fit mt-2"
          >
            Tentar novamente
          </ChonkyButton>
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="border rounded-lg bg-card shadow-sm transition-all hover:shadow-md">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Nome</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Integração</TableHead>
            <TableHead>Criado em</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(merchants as Merchant[])?.map((merchant) => (
            <TableRow key={merchant.id}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs uppercase">
                    {merchant.name.substring(0, 2)}
                  </div>
                  {merchant.name}
                </div>
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded w-fit">
                {merchant.slug}
              </TableCell>
              <TableCell>
                {merchant.guruAccountId ? (
                  <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                    Guru
                  </Badge>
                ) : merchant.yampiAlias ? (
                  <Badge variant="default" className="bg-purple-600 hover:bg-purple-700">
                    Yampi
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Nenhuma
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {new Date(merchant.createdAt).toLocaleDateString('pt-BR')}
              </TableCell>
            </TableRow>
          ))}
          {merchants?.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="h-48 text-center text-muted-foreground">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Package className="h-8 w-8 text-muted-foreground/50" />
                  <p>Nenhum lojista encontrado.</p>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <div className="border rounded-lg bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            {Array.from({ length: cols }).map((_, i) => (
              <TableHead key={i}>
                <Skeleton className="h-4 w-24" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              {Array.from({ length: cols }).map((_, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function UsersTable() {
  const { data: users, isLoading, error, refetch } = useUsers()

  if (isLoading) {
    return <TableSkeleton cols={5} />
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Erro ao carregar dados</AlertTitle>
        <AlertDescription className="flex flex-col gap-2">
          <p>{(error as Error).message}</p>
          <ChonkyButton
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            className="w-fit mt-2"
          >
            Tentar novamente
          </ChonkyButton>
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="border rounded-lg bg-card shadow-sm transition-all hover:shadow-md">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Nome</TableHead>
            <TableHead>E-mail</TableHead>
            <TableHead>Empresa</TableHead>
            <TableHead>Data Cadastro</TableHead>
            <TableHead className="w-[100px]">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(users as User[])?.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Badge variant="secondary">{user.merchant?.name || 'Sistema'}</Badge>
              </TableCell>
              <TableCell>{new Date(user.createdAt).toLocaleDateString('pt-BR')}</TableCell>
              <TableCell>
                {user.merchant?.slug !== 'rafobase-admin' ? (
                  <DeleteUserButton userId={user.id} userName={user.name} />
                ) : (
                  <span className="text-xs text-muted-foreground italic">Super Admin</span>
                )}
              </TableCell>
            </TableRow>
          ))}
          {users?.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center h-24">
                Nenhum usuário encontrado.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function DeleteUserButton({ userId, userName }: { userId: string; userName: string }) {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  const { mutate, isPending } = useMutation({
    mutationFn: () => api.backoffice.deleteUser(userId),
    onSuccess: () => {
      toast.success('Usuário excluído com sucesso')
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.backoffice.users })
      setOpen(false)
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir: ${error.message}`)
    },
  })

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600">
          <Trash2 className="size-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
          <AlertDialogDescription>
            Tem certeza que deseja excluir o usuário <strong>{userName}</strong>?
            <br />
            Essa ação não pode ser desfeita.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e: React.MouseEvent) => {
              e.preventDefault()
              mutate()
            }}
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={isPending}
          >
            {isPending ? 'Excluindo...' : 'Excluir'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
